import React, { useState, useEffect, useCallback, createRef, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GoogleMap, useJsApiLoader, MarkerF, InfoBox, Polyline, MarkerClusterer, Polygon, Circle } from "@react-google-maps/api";
import { INDEX_URL, GOOGLE_MAPS_KEY, icons, markers } from "../../helpers/constants";
import * as request from "../../services/FetchService";
import { darkMode, lightMode } from "./mapStyles";
import MapSettings from "./MapSettings";
import { MapTogglesProvider, useMapToggles } from "./ToggleContext";
import { useContextData } from "../ContextData";

import { fixDate, getCssVariableValue, getEventIconMap } from "../../helpers/helperFunctions";
import DeviceMarker from "./DeviceMarker";
import { useIntervalWhenVisible, useIsMobile, useLocalStorage } from "../../helpers/customHooks";
import HistoryMarker from "./HistoryMarker";
import EventMarker from "./EventMarker";
import MarkerTest from "./MarkerTest";
import { useGeofenceContext } from "./GeofenceMap/GeofenceContext";
import { useSidebarContext } from "../Sidebar/SiderbarContext";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import HistoryGraphNew from "../History/HistoryGraphNew";
import InfoSquare from "../History/InfoSquare";
import NewButton from "../Buttons/NewButton";
import toast from "react-hot-toast";

// import {darkMode, lightMode} from "./mapStyles.js"
const containerStyle = {
	height: "100%",
	borderRadius: "24px",
};
const clusterStyles = [
	{
		textColor: "white",
		url: markers.cluster_marker,
		height: 45,
		width: 40,
		anchorText: [-5, 0],
		anchorIcon: [45, 20],
		fontFamily: "Roboto",
		textSize: 12,
		fontWeight: "bold",
	},
];
const center = {
	lat: 59.30190008066058,
	lng: 18.104984453399872,
};

// Helpers for recency & coords
const toTs = (iso) => (iso ? Date.parse(iso) : -Infinity);
const getFixTs = (d) => toTs(d?.positioninfo?.datetime);
const hasCoords = (d) => d?.positioninfo?.latitude != null && d?.positioninfo?.longitude != null;

const MapComponent = (props) => {
	const {
		mapToggles,
		activeDevice,
		setActiveDevice,
		activeEvent,
		setActiveEvent,
		activeGeo,
		setActiveGeo,
		// creatingGeo,
		// setCreatingGeo,
		// contextGeoMethod,
		// updateGeoData,
		// contextSaveGeofence,
		// initialGeoData,
		// geofenceDeleted,
		// setGeofenceDeleted,
		// setGeofenceCreated,
		// geofenceCreated,
		mapTypes,
		historyData,
		setHistoryData,
		routesData,
		setRoutesData,
		groupArray,
		routePoint,
		setRoutePoint,
		hidePolyPart,
		setHidePolyPart,
	} = useMapToggles(); // Access the mapToggles state
	// const showPolyLine = useMemo(() => calculatePolyLine(), [historyPolyLine, mode]);
	// const { deviceListMap, setDeviceListMap } = useContextData();

	const { creating, setCreating, method, saveFence, setFenceData, initialData, geofenceDeleted, setGeofenceDeleted } = useGeofenceContext();
	const { t } = useTranslation();

	const [deviceList, setDeviceList] = useState([]);
	const [storage, setStorage] = useLocalStorage("mapsettings");
	const MOBILE = useIsMobile();
	const navigate = useNavigate();
	const location = useLocation();
	const [isOpen, setOpen] = useState(false);
	const [positions, setPositions] = useState([]);
	const [devices, setDevices] = useState(null);
	const [events, setEvents] = useState(null);
	const [geofences, setGeofences] = useState([]);
	const [map, setMap] = useState(null);
	const [mode, setMode] = useState(storage ? storage.maptype : "light");
	const [infoIndex, setIndex] = useState(null);
	const [toggle, setToggle] = useState(true);
	//const [looping, setLooping] = useState(false)
	const [visiblePolyGeo, setVisiblePolyGeo] = useState(null);
	const [visibleCircleGeo, setVisibleCircleGeo] = useState(null);
	const polygonRef = useRef(null);
	const listenersRef = useRef([]);
	const circleRef = useRef(null);
	const listenersCircleRef = useRef([]);
	const [polyPathData, setPolyPathData] = useState();
	const [circleData, setCircleData] = useState();
	const [openMarker, setOpenMarker] = useState(-1);
	const [activeGroup, setActiveGroup] = useState(null);
	const [historyPolyLine, setHistoryPolyLine] = useState();
	const [closestHistoryPoint, setClosestHistoryPoint] = useState();
	const [openEventMarker, setOpenEventMarker] = useState(-1);
	const [showDeviceName, setShowDeviceName] = useState(false);
	const [cluster, setCluster] = useState(false);
	const [hideOffline, setHideOffline] = useState(false);
	const [traceUnit, setTraceUnit] = useState(false);
	const [tracingPositions, setTracingPositions] = useState([]);
	const customColor = getCssVariableValue("--light_navy") ? getCssVariableValue("--light_navy") : "#000000";
	const customActiveColor = getCssVariableValue("--green") ? getCssVariableValue("--green") : "#000000";
	const [firstLoad, setFirstLoad] = useState(true);
	const { setActionbarContent, setTopbarContent, setTopbarTwoContent } = useSidebarContext();
	const [historyReplayView, setHistoryReplayView] = useState(true);

	const latestRequestRef = useRef(0);
	const lastPathRef = useRef(null);

	const { isLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: GOOGLE_MAPS_KEY,
		// libraries: ['geometry'], // <- add this
		// libraries: ["marker"],
	});
	const options = {
		minZoom: 1,
		restriction: {
			latLngBounds: {
				east: 179.9999,
				north: 85,
				south: -85,
				west: -179.9999,
			},
			strictBounds: true,
		},
		// styles: mode==="dark"?darkMode:lightMode,
		clickableIcons: false,
		mapTypeId: mode === "satellite" ? "satellite" : mode === "hybrid" ? "hybrid" : mode === "terrain" ? "terrain" : "roadmap",
		// mapId: "da381db21fff985f4e1232c0",
		mapTypeControl: false,
		streetViewControl: false,
		keyboardShortcuts: false,
		fullscreenControl: false,
		disableDefaultUI: true,
		tilt: 0,
		zoomControl: false,
		gestureHandling: "greedy",
		backgroundColor: "rgba(0,0,0,0)",
		styles: mode === "dark" ? darkMode : mode === "light" ? lightMode : null,
	};

	useEffect(() => {
		let activeMapType = mapTypes.find((item) => item.active === true);
		if (activeMapType) {
			setMode(activeMapType.id);
		}
		//setMode(activeMapType.id)
	}, [mapTypes]);

	// useEffect(() => {
	// 	if(!location.state && !location?.state?.history) return;
	// 	const history = location?.state?.history
	// 	const device = history?.device
	// 	const positions = history?.positions
	// 	const stats = history?.stats
	// 	setHistoryResponse({positions: positions, stats: stats, device: device})
	// 	setHistoryData(positions)
	// }, [])

	useEffect(() => {
		if (!historyData) return;
		historyContent();
	}, [historyData, historyReplayView, hidePolyPart]);

	const historyContent = () => {
		const closeHistory = () => {
			setHistoryData(null);
			setActionbarContent(null);
			setTopbarTwoContent(null);
			navigate("/devices", { state: { devId: historyData?.device?.id }, replace: true });
		};
		const replayContent = () => {
			if (!historyData) return;
			return <HistoryGraphNew data={historyData?.positions?.items} types={historyData?.positions?.fields} dataType="speed" />;
		};
		const detailContent = () => {
			const dev = historyData?.device;
			const stats = historyData?.stats;
			return (
				<div className="mt-3 mx-3">
					<div className="position-relative d-flex flex-column info-square justify-content-center align-items-start bg-white p-3">
						<img className="position-absolute top-0 end-0 primary-icon size_20 mt-2 me-2" src={icons.info_icon} alt="..." />
						<p>
							{t("history.device")}: <span className="bold">{dev?.devicename}</span>
						</p>
						<p>
							{t("history.from")}: <span className="bold">{fixDate(dev?.timestamps?.start)}</span> {t("history.to")}: <span className="bold">{fixDate(dev?.timestamps?.stop)}</span>
						</p>
					</div>
					<div className="row m-0 p-0 mt-3">
						{Object.entries(stats)
							.filter(([_, stat]) => stat.supported)
							.map(([key, stat], index, arr) => (
								<InfoSquare key={index} index={index} total={arr.length} spec={false} value={stat.value} label={stat.name} icon={key} />
							))}
					</div>
				</div>
			);
		};

		if(MOBILE){
			const actioncontent = (
				<motion.div key="menu" className="flex-grow-1 position-relative zlatan">
					{/* Scrollable content inside the glass nav */}
					<div className="h-100 overflow-auto mb-3">
						<div className="d-flex mx-3">
							<NewButton className={`full-rounded me-2 ${historyReplayView ? "bg-grey-blue white" : "bg-white grey-blue"}`} onClick={() => setHistoryReplayView(true)}>
								{t("history.replay")}
							</NewButton>
							<NewButton className={`full-rounded ${!historyReplayView ? "bg-grey-blue white" : "bg-white grey-blue"}`} onClick={() => setHistoryReplayView(false)}>
								{t("history.details")}
							</NewButton>
							{historyReplayView && <NewButton notext={true} icon={icons.route} iconClass={`${hidePolyPart ? "grey-blue-icon" : "white-icon"} size_15`} className={`ms-auto size36 full-rounded ${!hidePolyPart ? "bg-grey-blue" : "bg-white"}`} onClick={() => setHidePolyPart(!hidePolyPart)} />}
						</div>
						{historyReplayView ? replayContent() : detailContent()}
					</div>
				</motion.div>
			);
			const topcontent = {
				left: (
					<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={() => closeHistory()}>
						<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
					</div>
				),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t("devices.history")}
					</motion.h4>
				),
			};

			setTopbarContent(topcontent);
			setActionbarContent(actioncontent);
		}else{
			const actioncontent = (
				<motion.div key="menu" className="flex-grow-1 position-relative zlatan">
					{/* Scrollable content inside the glass nav */}
					<div className="h-100 overflow-auto mb-3">
						<div className="d-flex mx-3">
							<NewButton className={`full-rounded me-2 ${historyReplayView ? "bg-grey-blue white" : "bg-white grey-blue"}`} onClick={() => setHistoryReplayView(true)}>
								{t("history.replay")}
							</NewButton>
							<NewButton className={`full-rounded ${!historyReplayView ? "bg-grey-blue white" : "bg-white grey-blue"}`} onClick={() => setHistoryReplayView(false)}>
								{t("history.details")}
							</NewButton>
							{historyReplayView && <NewButton notext={true} icon={icons.route} iconClass={`${hidePolyPart ? "grey-blue-icon" : "white-icon"} size_15`} className={`ms-auto size36 full-rounded ${!hidePolyPart ? "bg-grey-blue" : "bg-white"}`} onClick={() => setHidePolyPart(!hidePolyPart)} />}
						</div>
						{historyReplayView ? replayContent() : detailContent()}
					</div>
				</motion.div>
			);
			const topcontent = {
				left: (
					<div className="cursor-pointer round-bg bg-white menu-button d-flex align-items-center justify-content-center" onClick={() => closeHistory()}>
						<motion.img key="close" src={icons.chevron_left} alt="close" className="size_15 primary-icon" initial={{ opacity: 1, rotate: 90 }} animate={{ opacity: 1, rotate: 0 }} exit={{ opacity: 1, rotate: 180 }} transition={{ duration: 0.2 }} />
					</div>
				),
				center: (
					<motion.h4 key="title" className="position-absolute top-50 start-50 translate-middle m-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
						{t("devices.history")}
					</motion.h4>
				),
				action: (
					actioncontent
				)
			};
			setTopbarContent(null);
			setTopbarTwoContent(topcontent)
		}


	};

	useEffect(() => {
		if (historyData?.positions) {
		//if (historyData?.polyline) {

		// const path = window.google.maps.geometry.encoding.decodePath(historyData?.polyline); // MVCArray<LatLng>
		// const points = path.map((latLng, i) => {
		// 	const [ts, speed] = historyData?.pointsInfo[i] || [];
		// 	return {
		// 	lat: latLng.lat(),
		// 	lng: latLng.lng(),
		// 	time: ts ? new Date(ts * 1000).toISOString() : undefined,
		// 	speed,
		// 	};
		// });
			const points = historyData?.positions?.items?.map((point) => ({
				lat: point.lat,
				lng: point.lon,
				time: point.time,
				speed: point.info?.speed,
				//unit: historyData.speedlimitunit,
				sat: point.sat,
				event: point.alarm,
				charge: point.charge,
				ignition: point.ignition,
			}));
			// const points = historyData?.points?.map((point) => ({
			// 	lat: point.latitude,
			// 	lng: point.longitude,
			// 	time: point.datetime,
			// 	speed: point.speed,
			// }));
			setHistoryPolyLine(points);
		} else {
			setHistoryPolyLine();
		}
	}, [historyData]);

	// useEffect(() => {
	// 	if (routesData) {
	// 		let points = routesData.map((point) => ({
	// 			lat: point.latitude,
	// 			lng: point.longitude,
	// 			time: point.datetime,
	// 			speed: point.speed,
	// 		}));
	// 		setHistoryPolyLine(points);
	// 	} else {
	// 		setHistoryPolyLine();
	// 	}
	// }, [routesData]);

	useEffect(() => {
		if (openMarker === -1 || !map || !devices?.length) return;
		const specificDevice = devices.find((d) => d.id === openMarker);
		const lat = specificDevice?.positioninfo?.latitude;
		const lng = specificDevice?.positioninfo?.longitude;
		if (lat == null || lng == null) return; // ⛑️ guard

		const pos = { lat, lng };
		if (map.getZoom() < 15) map.setZoom(15);
		map.panTo(pos);
		map.panBy(0, 150);
	}, [openMarker, map, devices]);

	useEffect(() => {
		if (groupArray.length > 0) {
			let filteredDevices = deviceList.filter((device) => groupArray.includes(device.id));
			setDevices(filteredDevices);
		} else {
			setDevices(deviceList);
		}
	}, [groupArray, deviceList]);

	useEffect(() => {
		if (firstLoad && devices?.length > 0) {
			setBounds();
			setFirstLoad(false);
		}
	}, [devices, map]);

	useEffect(() => {
		// if (!devices) {
		// 	getDevices();
		// }
		const handleStorage = () => setMode(localStorage.getItem("theme"));
		window.addEventListener("storage", handleStorage, false);
		return () => {
			window.removeEventListener("storage", handleStorage, false);
		};
	}, []);

	useEffect(() => {
		if (traceUnit && devices) {
			devices?.forEach((device) => {
				// Skip if lat or lng is null
				if (device.positioninfo?.latitude == null || device?.positioninfo?.longitude == null) {
					return;
				}

				let pos = { lat: device?.positioninfo?.latitude, lng: device?.positioninfo?.longitude };
				setTimeout(() => {
					setTracingPositions((prev) => {
						// Initialize device's array if it doesn't exist
						const devicePositions = prev[device.id] || [];

						// Check if the current position is the same as the last position in the array
						const lastPos = devicePositions[devicePositions.length - 1];
						if (lastPos && lastPos.lat === pos.lat && lastPos.lng === pos.lng) {
							// If the position is the same, do not modify the state
							return prev;
						}

						// Add new position or replace the oldest if there are already 10 positions
						const newPos = devicePositions.length >= 10 ? [...devicePositions.slice(1), pos] : [...devicePositions, pos];

						return {
							...prev,
							[device.id]: newPos, // Update the specific device's positions
						};
					});
				}, 1000);
			});
		} else {
			setTracingPositions([]);
		}
	}, [traceUnit, devices]);

	useEffect(() => {
		if (geofenceDeleted) {
			setTimeout(() => {
				setGeofenceDeleted(false);
				getGeofences();
			}, 250);
		}
	}, [geofenceDeleted]);

	// useEffect(() => {
	// 	if (geofenceDeleted || geofenceCreated) {
	// 		setTimeout(() => {
	// 			setGeofenceDeleted(false);
	// 			setGeofenceCreated(false);
	// 			getGeofences();
	// 		}, 250);
	// 	}
	// }, [geofenceDeleted, geofenceCreated]);

	const getEventsPost = () => {
		const data = {
			limit: "500",
			onlywithposition: true,
		};
		request.post("", "v2/events/list", data).then((response) => {
			const events = response.data.events;
			setEvents(events);
		});
	};

	const getGeofences = () => {
		request.get("geofence/info").then((response) => {
			if (response.data && response.status === 200) {
				setGeofences(response.data.response);
			}

			if (activeGeo) {
				const bounds = new window.google.maps.LatLngBounds();
				if (activeGeo.type == "POLYGON") {
					activeGeo.points?.map((point) => {
						let lat = point.latitude != null ? point.latitude : 0;
						let lng = point.longitude != null ? point.longitude : 0;
						bounds.extend({
							lat: parseFloat(lat),
							lng: parseFloat(lng),
						});
						return bounds;
					});
					if (bounds && map) {
						map.fitBounds(bounds);
					}
				} else {
					let latLng = new window.google.maps.LatLng(parseFloat(activeGeo.latitude), parseFloat(activeGeo.longitude));
					let circle = new window.google.maps.Circle({ radius: parseFloat(activeGeo.radius), center: latLng });
					if (bounds && map) {
						map.fitBounds(circle.getBounds());
					}
				}
			}
		});
	};

	useEffect(() => {
		setCreating(false);
		// if (location.pathname !== "/history" && (historyData || routesData)) {
		// 	setHistoryData();
		// 	setRoutesData();
		// }
		if (location.pathname !== "/events" && activeEvent) {
			setActiveEvent();
		}

		if (map) {
			if (activeGeo && MOBILE) {
				getGeofences();
			}
			const path = location.pathname;
			if (lastPathRef.current !== path) {
				lastPathRef.current = path;
				switch (path) {
					case "/devices":
						//updateData();
						break;
					case "/geofence":
						getGeofences();
						break;
					case "/history":
						break;
					default:
				}
			}
		}
	}, [location, map]);

	useEffect(() => {
		if (map) {
			mapToggles.forEach((toggle) => {
				switch (toggle.value) {
					case 1:
						if (toggle.active) {
							setActiveGroup(toggle.deviceArray);
						} else {
							setActiveGroup(null);
							updateData();
						}
						break;
					case 2:
						if (toggle.active) {
							getEventsPost();
							//	getEvents();
						} else {
							setEvents([]);
						}
						break;
					case 3:
						if (toggle.active) {
							if (geofences.length === 0) {
								getGeofences();
							}
						} else {
							if (location.pathname !== "/geofence") {
								setGeofences([]);
							}
						}
						break;
					case 4:
						setTraceUnit(toggle.active);
						break;
					case 5:
						setHideOffline(toggle.active);
						break;
					case 6:
						setCluster(toggle.active);
						break;
					case 7:
						setShowDeviceName(toggle.active);
						break;
					default:
						break;
				}
			});
		}
	}, [mapToggles]);

	useEffect(() => {
		const geo = mapToggles.findIndex((toggle) => toggle.value === 3);
		if (location.pathname !== "/geofence" && !mapToggles[geo].active) {
			setGeofences([]);
		}
	}, [location]);

	useEffect(() => {
		if (activeDevice) {
			const specificDevice = devices?.filter((device) => device.id === activeDevice);
			if (map && specificDevice[0]) {
				let lat = specificDevice[0]?.positioninfo?.latitude;
				let lng = specificDevice[0]?.positioninfo?.longitude;
				setOpenMarker(activeDevice);
				map.setZoom(15);
				map.panTo({ lat, lng });
			}
		}
	}, [activeDevice, map, devices]);

	useEffect(() => {
		if (!map || !creating) return;

		const bounds = new window.google.maps.LatLngBounds();

		// initialize when no initial data
		if (!initialData) {
			const lat = map.getCenter().lat();
			const lng = map.getCenter().lng();
			if (method === "circle") {
				const radius = 1000;
				setCircleData({
					center: { lat, lng },
					radius,
				});
				const latLng = new window.google.maps.LatLng(lat, lng);
				const circle = new window.google.maps.Circle({ radius, center: latLng });
				map.fitBounds(circle.getBounds());
				return;
			}
			if (method === "poly") {
				let path = [
					{
						lat: lat,
						lng: lng,
					},
					{
						lat: lat - 0.01,
						lng: lng + 0.02,
					},
					{
						lat: lat - 0.01,
						lng: lng - 0.02,
					},
				];
				setPolyPathData(path);
				path.map((point) => {
					let lat = point.lat;
					let lng = point.lng;
					bounds.extend({
						lat: parseFloat(lat),
						lng: parseFloat(lng),
					});
					return bounds;
				});
				if (bounds && map) {
					map.fitBounds(bounds);
				}
				return;
			}
			return;
		}

		// initialize when you DO have initial data
		if (method === "circle") {
			if (!initialData.center) {
				const radius = 1000;
				const lat = parseFloat(initialData[0].latitude);
				const lng = parseFloat(initialData[0].longitude);
				setCircleData({ radius, center: { lat, lng } });
				const latLng = new window.google.maps.LatLng(lat, lng);
				const circle = new window.google.maps.Circle({ radius, center: latLng });
				map.fitBounds(circle.getBounds());
			} else {
				const radius = parseFloat(initialData.radius);
				const { lat, lng } = initialData.center;
				setCircleData({ radius, center: { lat, lng } });
				const latLng = new window.google.maps.LatLng(parseFloat(lat), parseFloat(lng));
				const circle = new window.google.maps.Circle({ radius, center: latLng });
				map.fitBounds(circle.getBounds());
			}
		} else {
			let path = [];
			if (initialData.center) {
				path = [
					{
						lat: initialData.center.lat,
						lng: initialData.center.lng,
					},
					{
						lat: initialData.center.lat - 0.01,
						lng: initialData.center.lng + 0.02,
					},
					{
						lat: initialData.center.lat - 0.01,
						lng: initialData.center.lng - 0.02,
					},
				];
			} else {
				path = initialData.map((location) => ({
					lat: location.latitude,
					lng: location.longitude,
				}));
			}
			setPolyPathData(path);
			// HERE
			path.map((point) => {
				let lat = point.lat;
				let lng = point.lng;
				bounds.extend({
					lat: parseFloat(lat),
					lng: parseFloat(lng),
				});
				return bounds;
			});
			if (bounds && map) {
				map.fitBounds(bounds);
			}
		}
	}, [map, creating, method, initialData]);

	const showEventOnMap = (alarm) => {
		return <EventMarker event={alarm} isOpen={alarm.eventid === openEventMarker} clicked={() => setOpenEventMarker(openEventMarker === alarm.eventid ? -1 : alarm.eventid)} />;
	};

	const showGeofencesOnMap = () => {
		// if(creatingGeo){
		// 	return
		// }
		let circles = geofences?.filter((geofence) => geofence.type == "CIRCLE");
		let polys = geofences?.filter((geofence) => geofence.type == "POLYGON");
		return (
			<>
				{circles?.map((circle, index) => (
					<div key={index}>
						<Circle
							options={{
								fillColor: mode === "light" ? customColor : customColor,
								fillOpacity: "0.5",
								strokeColor: mode === "light" ? customColor : customColor,
								strokeWeight: "2",
								clickable: false,
							}}
							center={{ lat: circle.latitude, lng: circle.longitude }}
							radius={parseFloat(circle.radius)}
							onMouseOut={() => setVisibleCircleGeo(null)}
							onMouseOver={() => setVisibleCircleGeo(index)}
						/>
						{(visibleCircleGeo === index || location.pathname === "/geofence" || showDeviceName) && (
							<InfoBox
								position={{ lat: circle.latitude, lng: circle.longitude }}
								options={{
									disableAutoPan: true,
									closeBoxURL: "",
									boxStyle: {
										width: "auto",
										boxShadow: "0 3px 18px -8px rgba(0,0,0,0.15)",
										borderRadius: "5px",
									},
									pixelOffset: new window.google.maps.Size(-0, -0),
								}}
							>
								<div className="bg-black p-1 d-flex align-items-center">
									<span className="text-white">{circle.name}</span>
								</div>
							</InfoBox>
						)}
					</div>
				))}
				{polys?.map((poly, index) => {
					let centroid = poly.points.reduce(
						(acc, point) => {
							acc.lat += point.latitude;
							acc.lng += point.longitude;
							return acc;
						},
						{ lat: 0, lng: 0 }
					);

					centroid.lat /= poly.points.length;
					centroid.lng /= poly.points.length;
					return (
						<div key={index}>
							<Polygon
								options={{
									fillColor: mode === "light" ? customColor : customColor,
									fillOpacity: "0.5",
									strokeColor: mode === "light" ? customColor : customColor,
									strokeWeight: "2",
									clickable: false,
								}}
								paths={poly.points?.map((point) => ({
									lat: point.latitude,
									lng: point.longitude,
								}))}
								onMouseOut={() => setVisiblePolyGeo(null)}
								onMouseOver={() => setVisiblePolyGeo(index)}
							/>
							{(visiblePolyGeo === index || location.pathname === "/geofence" || showDeviceName) && (
								<InfoBox
									position={{ lat: centroid.lat, lng: centroid.lng }}
									options={{
										disableAutoPan: true,
										closeBoxURL: "",
										boxStyle: {
											width: "auto",
											boxShadow: "0 3px 18px -8px rgba(0,0,0,0.15)",
											borderRadius: "5px",
										},
										pixelOffset: new window.google.maps.Size(-25, -6),
									}}
								>
									<div className="bg-black p-1 d-flex align-items-center">
										<span className="text-white">{poly.name}</span>
									</div>
								</InfoBox>
							)}
						</div>
					);
				})}
			</>
		);
	};

	useEffect(() => {
		if (activeEvent && map) {
			let lat = activeEvent.latitude;
			let lng = activeEvent.longitude;
			setOpenEventMarker(activeEvent.eventid);
			if (map.getZoom() < 15) {
				map.setZoom(15);
			}
			map.panTo({ lat, lng });
			map.panBy(0, 150);
		}
	}, [activeEvent]);

	useEffect(() => {
		if (!activeGeo) {
			return;
		}
		if (MOBILE) {
			getGeofences();
			return;
		}

		const bounds = new window.google.maps.LatLngBounds();

		if (activeGeo.type == "POLYGON") {
			activeGeo.points?.map((point) => {
				let lat = point.latitude != null ? point.latitude : 0;
				let lng = point.longitude != null ? point.longitude : 0;
				bounds.extend({
					lat: parseFloat(lat),
					lng: parseFloat(lng),
				});
				return bounds;
			});
			if (bounds && map) {
				map.fitBounds(bounds);
			}
		} else {
			let latLng = new window.google.maps.LatLng(parseFloat(activeGeo.latitude), parseFloat(activeGeo.longitude));
			let circle = new window.google.maps.Circle({ radius: parseFloat(activeGeo.radius), center: latLng });
			if (bounds && map) {
				map.fitBounds(circle.getBounds());
			}
		}
	}, [activeGeo, map]);

	const updateData = useCallback(() => {
		const requestId = ++latestRequestRef.current;

		request.get("devices/map").then((response) => {
			if (requestId !== latestRequestRef.current) return;
			if (!response || !response.data || response.data.error || response.data.message) return;

			const newData = Array.isArray(response.data.devices) ? response.data.devices : [];

			// ✅ functional update with per-device regression guard
			setDeviceList((prev) => {
				const prevById = new Map(prev.map((d) => [d.id, d]));

				const next = newData.map((apiDev) => {
					const old = prevById.get(apiDev.id);
					if (!old) return apiDev;

					const oldTs = getFixTs(old);
					const newTs = getFixTs(apiDev);
					const missing = !hasCoords(apiDev);

					// Block regressions or missing coords from overwriting newer position
					if (newTs < oldTs || missing) {
						return {
							...old,
							...apiDev,
							positioninfo: {
								...old.positioninfo,
								...apiDev.positioninfo,
								latitude: old?.positioninfo?.latitude,
								longitude: old?.positioninfo?.longitude,
								datetime: old?.positioninfo?.datetime,
							},
						};
					}

					// If nothing material changed, keep old ref (reduces rerenders/flicker)
					const sameLat = old?.positioninfo?.latitude === apiDev?.positioninfo?.latitude;
					const sameLng = old?.positioninfo?.longitude === apiDev?.positioninfo?.longitude;
					const sameTs = oldTs === newTs;
					const sameStatus = old?.status === apiDev?.status;
					const sameMapIcon = old?.mapicon === apiDev?.mapicon;

 					if (sameLat && sameLng && sameTs && sameStatus && sameMapIcon) return old;
					return { ...old, ...apiDev };
				});

				// Keep stable order
				next.sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
				return next;
			});
		});
	}, []);

	/*  THIS IS THE ONE REAL OTHER IS TRIAL*/
	/*
	const updateData = useCallback(() => {
		// optional: keep if it helps you debug
		// console.log("LOOPING?");

		const requestId = ++latestRequestRef.current;

		request.get("devices/map").then((response) => {
			if (requestId !== latestRequestRef.current) {
				// console.log("STALE!!!");
				return;
			}
			if (!response || !response.data || response.data.error || response.data.message) return;

			const newData = response.data.devices;

			// ✅ use functional update to avoid stale closure
			setDeviceList((prev) => {
				return newData.map((apiDev) => {
					const old = prev.find((o) => o.id === apiDev.id);
					return old ? { ...old, ...apiDev } : apiDev;
				});
			});
		});
	}, []); // keep this stable so the interval doesn't reset
	*/

	const dontRun = location.pathname === "/subscription" || location.pathname === "/settings";
	useIntervalWhenVisible(updateData, 10_000, isLoaded && !dontRun);

	const onLoad = useCallback((map) => {
		setMap(map);
	}, []);

	const onUnmount = useCallback(() => {
		setMap(null);
		setActiveDevice(null);
		setActiveGeo(null);
	}, []);

	const setBounds = () => {

		if (!devices && map) {
			// map.setZoom(10)
			return;
		}
		if (activeGeo || activeDevice || historyData) return;
		if (map && devices?.length > 0) {
			const bounds = new window.google.maps.LatLngBounds();
			let hasValidDevice = false; // Track if at least one device was added to bounds

			devices.map((device) => {
				if (device?.positioninfo?.latitude && device?.positioninfo?.longitude) {
					bounds.extend({
						lat: parseFloat(device?.positioninfo?.latitude),
						lng: parseFloat(device?.positioninfo?.longitude),
					});
					hasValidDevice = true; // Found a valid device
				}
			});

			if (hasValidDevice) {
				map.fitBounds(bounds);

				if (devices.length === 1) {
					setTimeout(() => {
						map.setZoom(12);
					}, 0);
				}
			} else {
				// No valid devices, set center to (59, 18)
				map.setCenter({ lat: 59, lng: 18 });
				map.setZoom(7); // Optional: Set zoom level as needed
			}
		}
	};

	const expandMap = () => {
		const mapView = document.querySelector(".map-section");
		const mapViewPushed = document.querySelector(".map-section-pushed");
		const infoView = document.querySelector(".info-section");
		const infoViewPushed = document.querySelector(".info-section-pushed");
		if (infoView) {
			infoView?.classList.replace("info-section", "info-section-pushed");
			mapView?.classList.replace("map-section", "map-section-pushed");
		} else {
			mapViewPushed?.classList.replace("map-section-pushed", "map-section");
			infoViewPushed?.classList.replace("info-section-pushed", "info-section");
		}
		return;

		const map = document.querySelector(".map_style");
		const mapExpanded = document.querySelector(".map_style_expanded");
		const info = document.querySelector(".section-left_bottom_data");
		const infoExpanded = document.querySelector(".section-left_bottom_data_expanded");
		const statsCard = document.querySelector(".map_statistics_detail");
		const statsCardExtended = document.querySelector(".map_statistics_detail_expanded");
		if (map === null) {
			infoExpanded?.classList.replace("section-left_bottom_data_expanded", "section-left_bottom_data");
			if (mapExpanded) {
				mapExpanded?.classList.replace("map_style_expanded", "map_style");
			}
			if (statsCardExtended) {
				statsCardExtended?.classList.replace("map_statistics_detail_expanded", "map_statistics_detail");
			}
		} else {
			info?.classList.replace("section-left_bottom_data", "section-left_bottom_data_expanded");
			if (map) {
				map?.classList.replace("map_style", "map_style_expanded");
			}
			if (statsCard) {
				statsCard?.classList.replace("map_statistics_detail", "map_statistics_detail_expanded");
			}
		}
	};

	const onEditCircle = useCallback(() => {
		if (!circleRef.current) return;

		const center = circleRef.current.getCenter();
		const radius = circleRef.current.getRadius();

		// Snap the map circle so visuals match the rule
		if (radius < 50) {
			circleRef.current.setRadius(50);
		}

		setCircleData((prev) => {
			const adjustedRadius = Math.max(50, radius);

			const hasCenterChanged = prev.center.lat !== center.lat() || prev.center.lng !== center.lng();

			// Compare against the adjusted value to avoid loops
			const hasRadiusChanged = prev.radius !== adjustedRadius;

			// Only toast once when crossing below the threshold
			if (prev.radius >= 50 && radius < 50) {
				toast.error(t("map.circle_cant_be_smaller"));
			}

			if (!hasCenterChanged && !hasRadiusChanged) return prev;

			return {
				center: { lat: center.lat(), lng: center.lng() },
				radius: adjustedRadius,
			};
		});
	}, [setCircleData, circleRef]);

	// Bind refs to current Polygon and listeners
	const onLoadCircle = useCallback(
		(circle) => {
			circleRef.current = circle;
		},
		[onEditCircle]
	);

	// Clean up refs
	const onUnmountCircle = useCallback(() => {
		listenersCircleRef.current.forEach((lis) => lis.remove());
		circleRef.current = null;
	}, []);

	const creatingCircle = () => {
		return (
			<Circle
				options={{
					fillColor: mode === "light" ? customActiveColor : customActiveColor,
					fillOpacity: "0.5",
					strokeColor: mode === "light" ? customActiveColor : customActiveColor,
					strokeWeight: "2",
				}}
				editable
				//draggable
				center={circleData && circleData.center}
				radius={circleData && circleData.radius}
				onRadiusChanged={onEditCircle}
				onCenterChanged={onEditCircle}
				onMouseUp={onEditCircle}
				onDragEnd={onEditCircle}
				onLoad={onLoadCircle}
				onUnmount={onUnmountCircle}
			></Circle>
		);
	};

	const onEditPolygon = useCallback(() => {
		if (polygonRef.current) {
			const nextPath = polygonRef.current
				.getPath()
				.getArray()
				.map((latLng) => {
					return { lat: latLng.lat(), lng: latLng.lng() };
				});
			setPolyPathData(nextPath);
		}
	}, [setPolyPathData]);

	// Bind refs to current Polygon and listeners
	const onLoadPoly = useCallback(
		(polygon) => {
			polygonRef.current = polygon;
			const path = polygon.getPath();
			listenersRef.current.push(path.addListener("set_at", onEditPolygon), path.addListener("insert_at", onEditPolygon), path.addListener("remove_at", onEditPolygon));
		},
		[onEditPolygon]
	);

	// Clean up refs
	const onUnmountPoly = useCallback(() => {
		listenersRef.current.forEach((lis) => lis.remove());
		polygonRef.current = null;
	}, []);

	const creatingPoly = () => {
		return (
			<Polygon
				options={{
					fillColor: mode === "light" ? customActiveColor : customActiveColor,
					fillOpacity: "0.5",
					strokeColor: mode === "light" ? customActiveColor : customActiveColor,
					strokeWeight: "2",
				}}
				editable
				draggable
				path={polyPathData}
				onMouseUp={onEditPolygon}
				onDragEnd={onEditPolygon}
				onLoad={onLoadPoly}
				onUnmount={onUnmountPoly}
			></Polygon>
		);
	};

	useEffect(() => {
		if (!saveFence) return;
		console.log("TJO?")
		setFenceData(method === "circle" ? circleData : polyPathData);
		setTimeout(() => {
			getGeofences();
		}, 250);
	}, [saveFence]);
	// useEffect(() => {
	// 	if (contextSaveGeofence) {
	// 		if (contextGeoMethod === "poly") {
	// 			updateGeoData(polyPathData);
	// 		} else {
	// 			updateGeoData(circleData);
	// 		}
	// 	}
	// }, [contextSaveGeofence]);

	const showPolyInfo = (e, polylineBro) => {
		let clickLat = e.latLng.lat();
		let clickLng = e.latLng.lng();
		let closestPoint = historyPolyLine[0];
		let minDist = Number.MAX_VALUE;

		for (let point of polylineBro) {
			let dist = haversineDistance(clickLat, clickLng, point.lat, point.lng);
			if (dist < minDist) {
				minDist = dist;
				closestPoint = point;
			}
		}

		const index = historyPolyLine.findIndex((p) => p.lat === closestPoint.lat && p.lng === closestPoint.lng);

		if (closestHistoryPoint !== closestPoint) {
			setRoutePoint(index);
			//setClosestHistoryPoint(closestPoint)
			//map.panTo({lat: closestPoint.lat, lng: closestPoint.lng})
		} else {
			setClosestHistoryPoint(null);
		}
	};

	useEffect(() => {
		if (historyPolyLine && map) {
			const bounds = new window.google.maps.LatLngBounds();
			historyPolyLine.map((point, index) => {
				var lat = point.lat != null && point.lat;
				var lng = point.lng != null && point.lng;
				bounds.extend({
					lat: parseFloat(lat),
					lng: parseFloat(lng),
				});
				return bounds;
			});
			if (bounds && map) {
				map.fitBounds(bounds);
			}
		}
	}, [historyPolyLine, historyData, map]);

	const haversineDistance = (lat1, lng1, lat2, lng2) => {
		const toRad = (x) => (x * Math.PI) / 180;
		const R = 6371; // Earth radius in km

		const dLat = toRad(lat2 - lat1);
		const dLng = toRad(lng2 - lng1);
		const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		return R * c; // Distance in km
	};

	const polyLineContent = useMemo(() => {
		if (!historyPolyLine || historyPolyLine.length === 0) return null;
		if (!window.google || !window.google.maps || !window.google.maps.Size) return null;
		return (
			<>
				<MarkerF
					icon={{
						url: markers.history_start,
						size: new window.google.maps.Size(34, 56), // Setting the size here
						scaledSize: new window.google.maps.Size(34, 56), // Optional, for scaling the icon
						anchor: new window.google.maps.Point(17, 56), // Set the anchor point to the center of the icon
					}}
					onClick={(e) => showPolyInfo(e, [historyPolyLine[0], historyPolyLine[0]])}
					position={{ lat: historyPolyLine[0].lat, lng: historyPolyLine[0].lng }}
				/>
				<MarkerF
					icon={{
						url: markers.history_stop,
						size: new window.google.maps.Size(34, 56), // Setting the size here
						scaledSize: new window.google.maps.Size(34, 56), // Optional, for scaling the icon
						anchor: new window.google.maps.Point(17, 56), // Set the anchor point to the center of the icon
					}}
					onClick={(e) => showPolyInfo(e, [historyPolyLine[historyPolyLine.length - 1], historyPolyLine[historyPolyLine.length - 1]])}
					position={{ lat: historyPolyLine[historyPolyLine.length - 1].lat, lng: historyPolyLine[historyPolyLine.length - 1].lng }}
				/>
				{historyPolyLine.map((point, index) => {
					if (hidePolyPart === true && index >= routePoint) return null; // Skip rendering after routePoint

					let polylinePoints;
					let elements = [];

					if (index === 0) {
						polylinePoints = [point, historyPolyLine[index + 1], historyPolyLine[index + 2]];
					} else if (index === historyPolyLine.length - 2) {
						polylinePoints = [historyPolyLine[index - 1], point, historyPolyLine[index + 1]];
					} else if (index === historyPolyLine.length - 1) {
						return null;
					} else {
						polylinePoints = [point, historyPolyLine[index + 1]];
					}

					const polylineElement = (
						<Polyline
							key={`polyline-${index}`}
							options={{
								fillColor: customColor,
								fillOpacity: "1",
								strokeColor: customColor,
								strokeWeight: "4",
								geodesic: false,
							}}
							onClick={(e) => showPolyInfo(e, polylinePoints)}
							path={polylinePoints}
						/>
					);

					elements.push(polylineElement);

					if (point.speed === 0 && map) {
						const markerElement = (
							<MarkerF
								key={`marker-${index}`}
								icon={{
									url: icons.stopped_marker,
									size: new window.google.maps.Size(8, 8),
									scaledSize: new window.google.maps.Size(8, 8),
									anchor: new window.google.maps.Point(4, 4),
								}}
								onClick={(e) => showPolyInfo(e, polylinePoints)}
								position={{ lat: point.lat, lng: point.lng }}
							/>
						);
						elements.push(markerElement);
					}

					return elements;
				})}
			</>
		);
	}, [historyPolyLine, mode, routePoint, hidePolyPart]);

	// put this somewhere reusable
	function panToWithOffset(map, latLng, offsetX, offsetY) {
		const ov = new window.google.maps.OverlayView();
		ov.onAdd = () => {};
		ov.draw = () => {
			const proj = ov.getProjection();
			if (!proj) return;

			// current center in div pixels
			const center = map.getCenter();
			const centerPx = proj.fromLatLngToDivPixel(center);

			// where the target currently appears in div pixels
			const targetPx = proj.fromLatLngToDivPixel(latLng);

			// how much to move the center so target appears offset by (offsetX, offsetY)
			const dx = targetPx.x - centerPx.x - offsetX;
			const dy = targetPx.y - centerPx.y - offsetY;

			// new center = old center + delta
			const newCenter = proj.fromDivPixelToLatLng(new window.google.maps.Point(centerPx.x + dx, centerPx.y + dy));

			map.setCenter(newCenter);
			ov.setMap(null); // cleanup
		};
		ov.onRemove = () => {};
		ov.setMap(map);
	}

	useEffect(() => {
		if (routePoint === -1 || !map || !historyPolyLine) {
			setClosestHistoryPoint(null);
			return;
		}

		const pt = historyPolyLine[routePoint];
		if (!pt) return;

		setClosestHistoryPoint(pt);

		const target = new window.google.maps.LatLng(pt.lat, pt.lng);

		if (MOBILE) {
			map.setZoom(12); // set desired zoom first
			const h = map.getDiv().offsetHeight;

			// Put the point at ~2/3 down the screen:
			// offsetY < 0 moves the map content up, so the point appears lower.
			panToWithOffset(map, target, 0, Math.round(h / 4 - 20));
		} else {
			map.panTo(target);
		}
	}, [routePoint, map, historyPolyLine, MOBILE]);

	const eventsOnMap = useMemo(() => {
		if (!events || events.length === 0) {
			return null;
		}
		return (
			events &&
			events.map((event, index) => {
				return <EventMarker key={index} event={event} isOpen={event.eventid === openEventMarker} clicked={() => setOpenEventMarker(openEventMarker === event.eventid ? -1 : event.eventid)} />;
			})
		);
	}, [events, openEventMarker]);

	useEffect(() => {
		if (openMarker === -1 && !MOBILE) {
			setActiveDevice(null);
		}
	}, [openMarker]);

	return (
		isLoaded &&
		devices !== null && (
			<div className={`map-section`}>
				{/* <div className="map_style"> */}
				<GoogleMap mapContainerStyle={containerStyle} /* center={center} zoom={2} */ onLoad={onLoad} onUnmount={onUnmount} options={options}>
					<MapSettings />
					{/* EXPAND MAP BUTTON */}
					{/* <button className={`btn expand_map ${MOBILE ? "d-none" : ""}`} onClick={() => expandMap()}>
						<img src={icons.expand} className="expand_map_icon white-icon cursor-pointer" alt="..." />
					</button> */}
					{/*SHOW DEVICES*/}
					{cluster ? (
						<MarkerClusterer averageCenter gridSize={40} maxZoom={15} minimumClusterSize={2} styles={clusterStyles}>
							{(clusterer) =>
								devices?.length > 0 &&
								devices?.map((device) => {
									return <DeviceMarker hideOffline={hideOffline} clusterer={clusterer} key={`${device.id}-${device.status}-${device.mapicon}`} device={device} isOpen={device.id === openMarker} clicked={() => setOpenMarker(openMarker === device.id ? -1 : device.id)} showName={showDeviceName} />;
								})
							}
						</MarkerClusterer>
					) : (
						devices?.length > 0 &&
						// devices?.map((device) => {
						// 	return (
						// 		<MarkerTest
						// 		map={map}
						// 		position={{ lat: device?.positioninfo?.latitude ?? 0, lng: device?.positioninfo?.longitude ?? 0 }}

						// 		hideOffline={hideOffline}
						// 		activeDevice={activeDevice}
						// 		key={device.id}
						// 		device={device}
						// 		isOpen={device.id === openMarker}
						// 		clicked={() => setOpenMarker(openMarker === device.id ? -1 : device.id)}
						// 		showName={showDeviceName}
						// 		>
						// 			<div className="marker">
						// 				<h2>{device.name}</h2>
						// 				</div>
						// 			</MarkerTest>
						// 	)
						// })
						devices?.map((device) => {
							return <DeviceMarker hideOffline={hideOffline} activeDevice={activeDevice} key={`${device.id}-${device.status}-${device.mapicon}`} device={device} isOpen={device.id === openMarker} clicked={() => setOpenMarker(openMarker === device.id ? -1 : device.id)} showName={showDeviceName} />;
						})
					)}

					{traceUnit &&
						Object.entries(tracingPositions).map(
							(lol, index) =>
								lol[1].length > 1 && (
									<Polyline
										key={`polyline-${index}`}
										options={{
											fillColor: mode === "light" || mode === "terrain" ? customColor : customColor,
											fillOpacity: "1",
											strokeColor: mode === "light" || mode === "terrain" ? customColor : customColor,
											strokeWeight: "4",
											geodesic: false,
										}}
										path={lol[1]}
									/>
								)
						)}

					{/*SHOW SPECIFIC EVENT ON MAP*/}
					{activeEvent && showEventOnMap(activeEvent)}
					{/* SHOW GEOFENCES ON MAP*/}
					{showGeofencesOnMap()}
					{/* SHOW CREATING GEOFENCE*/}
					{creating && method === "circle" ? creatingCircle() : null}
					{creating && method === "poly" ? creatingPoly() : null}
					{/* SHOW EVENTS ON MAP */}
					{eventsOnMap}
					{polyLineContent}
					{closestHistoryPoint && polyLineContent && <HistoryMarker point={closestHistoryPoint} closeWindow={() => setClosestHistoryPoint(null)} />}
					<></>
				</GoogleMap>
				{/* </div> */}
			</div>
		)
	);
};

export default MapComponent;
